// src/components/auth/RegistrationSuccess.js
import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { CheckCircle, ArrowRight, Mail, Calendar, CreditCard, Loader } from 'lucide-react';
import './RegistrationSuccess.css';

function RegistrationSuccess() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const sessionId = searchParams.get('session_id');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [subscriptionDetails, setSubscriptionDetails] = useState(null);

  useEffect(() => {
    // Verify the session with backend
    const verifySubscription = async () => {
      if (!sessionId) {
        setError('Invalid session. Please contact support.');
        setLoading(false);
        return;
      }

      try {
        const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8009/api';
        
        const response = await fetch(`${API_URL}/auth/verify-registration/?session_id=${sessionId}`);
        const data = await response.json();
        
        if (response.ok) {
          setSubscriptionDetails(data);
        } else {
          setError(data.error || 'Verification failed');
        }
      } catch (err) {
        console.error('Verification error:', err);
        setError('Could not verify subscription. Your account is created, but please contact support.');
      } finally {
        setLoading(false);
      }
    };

    verifySubscription();
  }, [sessionId]);

  if (loading) {
    return (
      <div className="success-container">
        <div className="success-card">
          <Loader className="spinner-icon" size={60} />
          <h2>Confirming your subscription...</h2>
          <p>Please wait while we set up your account.</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="success-container">
        <div className="success-card error-card">
          <div className="error-icon">⚠️</div>
          <h1>Something Went Wrong</h1>
          <p>{error}</p>
          <div className="action-buttons">
            <button onClick={() => navigate('/login')} className="btn-primary">
              Try Logging In
            </button>
            <button onClick={() => window.location.href = 'mailto:support@taalumaerp.com'} className="btn-secondary">
              Contact Support
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="success-container">
      <div className="success-card">
        {/* Success Icon */}
        <div className="success-icon">
          <CheckCircle size={80} />
        </div>
        
        <h1>Welcome to TaalumaERP! 🎉</h1>
        <p className="success-subtitle">
          Your account has been created successfully and your 14-day free trial has started.
        </p>

        {/* Key Information */}
        <div className="info-grid">
          <div className="info-item">
            <Calendar size={24} />
            <div>
              <h3>Trial Period</h3>
              <p>14 days free access to all features</p>
              {subscriptionDetails?.trial_end && (
                <small>Ends on {new Date(subscriptionDetails.trial_end).toLocaleDateString()}</small>
              )}
            </div>
          </div>
          
          <div className="info-item">
            <Mail size={24} />
            <div>
              <h3>Check Your Email</h3>
              <p>We've sent login credentials to:</p>
              {subscriptionDetails?.email && (
                <small><strong>{subscriptionDetails.email}</strong></small>
              )}
            </div>
          </div>
          
          <div className="info-item">
            <CreditCard size={24} />
            <div>
              <h3>No Charges Yet</h3>
              <p>You'll be charged after the trial period</p>
              {subscriptionDetails?.amount && subscriptionDetails?.currency && (
                <small>
                  {subscriptionDetails.currency === 'usd' ? '$' : subscriptionDetails.currency.toUpperCase()} 
                  {subscriptionDetails.amount}/month
                </small>
              )}
            </div>
          </div>
        </div>

        {/* Next Steps */}
        <div className="next-steps">
          <h2>What's Next?</h2>
          <ol>
            <li>
              <strong>Check your email</strong> for login credentials
              <br />
              <small>Look for an email from noreply@taalumaerp.com</small>
            </li>
            <li>
              <strong>Log in</strong> to your account using the credentials
            </li>
            <li>
              <strong>Set up your first warehouse</strong> and add inventory
            </li>
            <li>
              <strong>Invite team members</strong> to collaborate
            </li>
            <li>
              <strong>Start managing</strong> your stock and orders!
            </li>
          </ol>
        </div>

        {/* Action Buttons */}
        <div className="action-buttons">
          <button onClick={() => navigate('/login')} className="btn-primary">
            Go to Login
            <ArrowRight size={20} />
          </button>
          <button 
            onClick={() => window.open('https://docs.taalumaerp.com', '_blank')} 
            className="btn-secondary"
          >
            View Documentation
          </button>
        </div>

        <p className="support-text">
          Need help? <a href="mailto:support@taalumaerp.com">Contact our support team</a>
        </p>

        {/* Session ID for debugging (optional, remove in production) */}
        {sessionId && (
          <details className="debug-info">
            <summary>Session Info (for support)</summary>
            <code>{sessionId}</code>
          </details>
        )}
      </div>
    </div>
  );
}

export default RegistrationSuccess;
